// ============================================================
// SALT — Dashboard Helpers
// Shared formatters, icons, and utilities for dashboard components
// ============================================================

import React from 'react';
import type { SeasonStatus, MatchFormat } from '../../types/dashboard.types';
import saltEmblem from '../../assets/salt-emblem.png';

// ── Date / Time Formatters ─────────────────────────────────

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatScheduledTime(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

export function getTimeRemaining(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'Expired';

  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m left`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;

  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

export function getRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;

  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;

  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// ── Season Status Helpers ──────────────────────────────────

export function getStatusLabel(status: SeasonStatus): string {
  switch (status) {
    case 'in_progress': return 'In Progress';
    case 'upcoming': return 'Upcoming';
    case 'completed': return 'Completed';
    case 'archived': return 'Archived';
    default: return status;
  }
}

export function getStatusClass(status: SeasonStatus): string {
  switch (status) {
    case 'in_progress': return 'sl-season-status-main--active';
    case 'completed':
    case 'archived': return 'sl-season-status-main--complete';
    default: return '';
  }
}

export function getStatusNote(status: SeasonStatus): string {
  switch (status) {
    case 'in_progress': return 'Ranked ladder is live.';
    case 'upcoming': return 'Season starts soon.';
    case 'completed': return 'Season has ended.';
    case 'archived': return 'Archived season.';
    default: return '';
  }
}

// ── Match Formatters ───────────────────────────────────────

export function formatMatchType(type: string): string {
  switch (type) {
    case 'available_now': return 'Available Now';
    case 'scheduled': return 'Scheduled';
    case 'direct_challenge': return 'Direct Challenge';
    default: return type;
  }
}

export function formatMatchFormat(format: MatchFormat): string {
  return format.toUpperCase().replace('BO', 'Bo');
}

// ── Rank Styling ───────────────────────────────────────────

export function getRankClass(rank: number | null): string {
  if (rank === 1) return 'sl-leaderboard-rank--gold';
  if (rank === 2) return 'sl-leaderboard-rank--silver';
  if (rank === 3) return 'sl-leaderboard-rank--bronze';
  return '';
}

// ── Fallback Icons ─────────────────────────────────────────

/** Default team icon when no logo is available (24px context) */
export const DefaultTeamIcon: React.FC = () => (
  <img
    src={saltEmblem}
    alt="Default team logo"
    className="sl-leaderboard-team-logo"
  />
);

/** Small team icon for compact rows (14px) */
export const DefaultTeamIconSmall: React.FC = () => (
  <img
    src={saltEmblem}
    alt=""
    width={14}
    height={14}
    style={{ opacity: 0.85, objectFit: 'contain' }}
  />
);